// Canonical UI paths for capabilities that used to live under /enterprise/*.
// Keep legacy paths as aliases so bookmarks and older module grants continue
// to work while the sidebar exposes each capability inside its owning module.
export const MODULE_PATH_ALIASES = {
  // NOTE: /creative/picture (Picture editor) and /creative/library (Asset Library)
  // are DISTINCT pages/modules — never alias them to each other. A bidirectional
  // alias compiles into two mutually-redirecting <Navigate> routes (an infinite
  // redirect loop that renders a blank page and shadows both real routes).
  // NOTE 2: /dtdc-courier and /bluedart-courier are LIVE pages — they share the
  // Courier grant via MODULE_GRANT_CLUSTERS below (no redirect). Listing them
  // here compiled into <Navigate> routes that shadowed the real pages, so the
  // command palette's "Blue Dart Courier" hit bounced to the generic /courier.
  '/system/autonomy': ['/enterprise/autonomy'],
  '/system/global-systems': ['/enterprise/global-systems'],
  '/master-data-governance': ['/enterprise/master-data-governance'],
  '/customer-communication': ['/enterprise/customer-communication'],
  '/workflow-builder': ['/enterprise/workflow-builder'],
  '/grc': ['/enterprise/grc', '/enterprise/grc-center'],
  '/profitability-engine': ['/enterprise/profitability-engine'],
  '/advanced-warehouse': ['/enterprise/advanced-warehouse'],
  '/procurement-hub': ['/enterprise/procurement-hub'],
  '/finance-controls': ['/enterprise/finance-controls'],
  '/transport-management': ['/enterprise/transport-management'],
  '/asset-maintenance': ['/enterprise/asset-maintenance'],
  '/quality-compliance': ['/enterprise/quality-compliance'],
  '/ai-layer': ['/enterprise/ai-layer'],
  // Retired carton pages (2026-07): specs folded into Packaging Materials
  // (type master_carton), receipts into Packaging Inwards. Aliases keep old
  // bookmarks and lingering module grants working.
  '/packaging-materials': ['/packaging-boxes'],
  '/pkg-inwards': ['/carton-inwards'],
  // Read-only fragrance where-used view — served by the Formulations module grant
  // (no separate module to provision; founders bypass anyway).
  '/formulations': ['/fragrance-registry', '/fragrance-notes', '/costing-coverage'],
};

// Alias paths that are LIVE PAGES, not old names.
//
// MODULE_PATH_ALIASES answers "which grant covers this page". It is also
// compiled into <Navigate> redirects in App.js, and those are two different
// questions. A page that merely SHARES a grant is not a page that MOVED — but
// listing it here redirected it away from itself, shadowing its real route.
//
// This has now happened three times: /creative/picture, /dtdc-courier and
// /bluedart-courier (see the notes above), and /fragrance-notes +
// /fragrance-registry — searching "fragrance notes" opened Formulations,
// because Formulations is the grant that covers it. Rather than a fourth
// comment, membership here removes a path from redirect generation while
// keeping its grant coverage intact. appRouteAliases.test.js derives the same
// answer from App.js's real routes and fails the build if the two disagree.
export const LIVE_PAGE_ALIASES = new Set([
  '/fragrance-registry',
  '/costing-coverage',
  '/fragrance-notes',
]);

export const LEGACY_TO_CANONICAL_MODULE_PATH = Object.fromEntries(
  Object.entries(MODULE_PATH_ALIASES)
    .flatMap(([canonical, legacyPaths]) => legacyPaths.map((legacy) => [legacy, canonical]))
);

// ── Grant clusters (2026-07-15 module consolidation) ────────────────────────
// One catalog entry now covers a family of sibling pages. Unlike
// MODULE_PATH_ALIASES these do NOT create redirects — every member page stays
// reachable; holding a grant on ANY member opens ALL members. Keep each
// cluster mirrored in backend auth.js MODULE_EQUIVALENTS.
export const MODULE_GRANT_CLUSTERS = [
  // ── 2026-09-12 merges: a grant on the retired module opens its new home ──
  ['/quality-compliance', '/capa', '/supplier-audits', '/regulatory', '/documents'],
  ['/costing-coverage', '/costing'],
  ['/sales-channels', '/integrations/indiamart'],
  ['/sales', '/forecast'],
  ['/social', '/linkedin'],
  ['/creative/approval-inbox', '/creative/requests'],
  ['/affiliate', '/loyalty'],
  ['/360', '/sku360', '/vendor360', '/client360', '/customer-360'],
  ['/salesforce', '/salesforce/follow-ups', '/salesforce/pipeline', '/salesforce/sequences'],
  ['/investor-readiness', '/investor-readiness/sku-master', '/investor-readiness/channel-mapping',
    '/investor-readiness/unit-economics', '/investor-readiness/brand-pl', '/investor-readiness/inventory-wc',
    '/investor-readiness/compliance-vault', '/investor-readiness/cohort-analytics',
    '/investor-readiness/data-room', '/investor-readiness/gap-audit'],
  ['/reseller-portal', '/reseller-vendors', '/reseller-orders'],
  ['/rm-inwards', '/pkg-inwards', '/label-inwards'],
  ['/qms/incoming', '/qms/inprocess', '/qms/release'],
  ['/creative/freepik', '/creative/picture', '/creative/remove-background', '/creative/product-cutout',
    '/creative/ai-image-gen', '/creative/vectorize', '/creative/video-tr', '/creative-studio'],
  ['/email-campaigns', '/email', '/bulk-mailer'],
  ['/automations', '/wa-automation'],
  ['/babeology', '/bscly', '/perfumery'],
  ['/packaging', '/packaging-orders'],
  ['/artwork', '/labels', '/jobs'],
  ['/courier', '/dtdc-courier', '/bluedart-courier', '/returns-intelligence', '/transport-management'],
  // ── 2026-07-16 reduction sweep ──
  ['/brands', '/trademarks'],
  ['/customer-care', '/service-tickets', '/complaints', '/knowledge-base'],
  ['/shopify-stores', '/shopify-dashboard'],
  ['/marketplace', '/amazon'],
  ['/buyer-admin', '/buyer-pos', '/buyer-sku-aliases', '/buyer-grn', '/b2b-sell-through'],
  ['/creative/copy-bank', '/creative/teleprompter', '/creative/voiceover', '/creative/shot-planner', '/creative/post-tracker',
    '/creative/prompt-library'],
  ['/creative/library', '/creative/brand-assets', '/creative/color-library',
    '/creative/photography', '/creative/collections', '/creative/templates', '/creatives', '/design/patterns'],
  ['/market-reference', '/ewg'],
  ['/batch-codes', '/coa-generator'],
  ['/shopfloor', '/line-schedule'],
  ['/warehouse', '/finished-goods'],
  ['/dispatch', '/packing-list', '/label-print', '/dispatch-ops'],
  ['/opportunity-radar', '/npd-recommender', '/benchmark-index', '/trend-feed'],
  ['/reports', '/daily-reports', '/report-delivery', '/ask', '/stalled-work', '/data-quality'],
  ['/system/cron-panel', '/system/connector-health'],
  ['/settings', '/settings/announcements'],
  ['/vendor-dashboard', '/vendor-doc-vault'],
  // ── 2026-07-17 ──
  ['/quotations', '/cpq-rules'],
  ['/sales-catalog', '/legacy-sales'],
  // ── 2026-08-05 ──
  // Routes whose URL differs from the module they're guarded by. <ModuleGuard>
  // uses the module path, but Layout's outer route guard checks the URL path —
  // so without these the holder of the correct grant still hit the "Access
  // Denied" padlock. Clusters (not MODULE_PATH_ALIASES) because these are live
  // distinct URLs: aliasing would compile into <Navigate> routes that shadow
  // the real pages (see the note at the top of this file).
  ['/qc', '/qc-gate'],
  ['/skus', '/skus-legacy'],
  ['/creative/influencers', '/squad-insights-import', '/squad-creator'],
];

const CLUSTER_BY_PATH = {};
for (const cluster of MODULE_GRANT_CLUSTERS) {
  for (const p of cluster) CLUSTER_BY_PATH[p] = cluster;
}

// One-directional umbrella: a '/finance' grant opens every consolidated
// finance view, but a narrow view grant (e.g. '/day-book') does NOT open the
// rest of Finance. Mirrors backend MODULE_ALIAS_MAP/'/finance' coverage.
export const FINANCE_UMBRELLA_VIEWS = [
  '/order-to-cash', '/client-ledger', '/day-book', '/debit-notes', '/vendor-payables',
  '/tally-export', '/margin-simulator', '/profitability-engine', '/general-ledger',
  '/ar-aging', '/credit-notes', '/cash-flow', '/itr-records', '/cc', '/credit-control',
  '/marketplace-finance', '/marketplace-mapping',
];

// What App.js should actually turn into <Navigate> routes: genuine renames
// only. Grant-sharing live pages keep their own routes.
export const LEGACY_PATH_REDIRECTS = Object.fromEntries(
  Object.entries(LEGACY_TO_CANONICAL_MODULE_PATH)
    .filter(([legacyPath]) => !LIVE_PAGE_ALIASES.has(legacyPath))
);

export function equivalentModulePaths(rawPath) {
  const path = String(rawPath || '').split('?')[0];
  const canonical = LEGACY_TO_CANONICAL_MODULE_PATH[path] || path;
  const out = new Set([canonical, ...(MODULE_PATH_ALIASES[canonical] || [])]);
  for (const p of [...out]) {
    (CLUSTER_BY_PATH[p] || []).forEach((m) => out.add(m));
    if (FINANCE_UMBRELLA_VIEWS.includes(p)) out.add('/finance');
  }
  return [...out];
}
